import { apiFetch } from "@/lib/api";
import type { MeDto, TimeZoneDetectionDto } from "@/lib/types";
import { useNavigate } from "react-router-dom";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { browserTimeZone } from "@/lib/time-zone";

interface SessionContextValue {
  me: MeDto | null;
  loading: boolean;
  isAdmin: boolean;
  refresh: () => Promise<void>;
  signout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
}

export function SessionProvider({
  initialMe = null,
  children,
}: {
  initialMe?: MeDto | null;
  children: ReactNode;
}) {
  const [me, setMe] = useState<MeDto | null>(initialMe);
  const [loading, setLoading] = useState(initialMe === null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<MeDto>("/api/auth/me", {
        suppressAuthRedirect: true,
      });
      setMe(data);
    } catch {
      setMe({ authenticated: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialMe === null) {
      void refresh();
    }
  }, [initialMe, refresh]);

  // Accounts created before the time zone was optional all carry a literal "UTC" that nobody
  // picked, and the tracker counts their days in UTC — so someone in PDT logging after 17:00 has
  // it recorded against tomorrow. Report what the browser actually says; the server adopts it only
  // for accounts that never chose, and ignores it otherwise.
  const timeZoneReported = useRef(false);
  useEffect(() => {
    if (!me?.authenticated || timeZoneReported.current) return;
    const chosen = me.timeZoneId;
    if (chosen && chosen !== "UTC") return;
    const detected = browserTimeZone();
    if (detected === "UTC" || detected === chosen) return;

    timeZoneReported.current = true;
    void (async () => {
      try {
        const result = await apiFetch<TimeZoneDetectionDto>(
          "/api/profile/detected-time-zone",
          {
            method: "POST",
            body: JSON.stringify({ timeZoneId: detected }),
            suppressAuthRedirect: true,
          },
        );
        if (result.adopted) await refresh();
      } catch {
        // A wrong-but-usable zone beats blocking the app on a best-effort correction.
      }
    })();
  }, [me?.authenticated, me?.timeZoneId, refresh]);

  const signout = useCallback(async () => {
    try {
      await apiFetch("/api/auth/signout", { method: "POST", suppressAuthRedirect: true });
    } catch {
      // Ignore; clearing local state is what matters.
    }
    setMe({ authenticated: false });
    navigate("/");
  }, [navigate]);

  const isAdmin = me?.authenticated === true && me.role === "Administrator";

  // The session wraps the entire app, so a fresh object here would re-render every
  // consumer on each render of any ancestor.
  const value = useMemo(
    () => ({ me, loading, isAdmin, refresh, signout }),
    [me, loading, isAdmin, refresh, signout],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}
